import { useState, type CSSProperties } from "react";
import { Link, useParams } from "react-router-dom";
import s from "./Projects.module.css";
import { GateDecisionDialog } from "./GateDecisionDialog";
import { ProjectLogCard } from "./ProjectLogCard";
import { EditProjectDialog, ScheduleDialog } from "./ProjectForms";
import { ScheduleCell } from "./ProjectsPage";
import { Page } from "../../layout/AppShell";
import { useSession } from "../../app/SessionContext";
import { useProject, useSubmitGate } from "../../api/queries";
import { hasPermission, PERMISSION } from "../../api/dataSource";
import {
  canSubmitGate,
  formatMoney,
  GATE_STATUS_LABEL,
  INSPECTION_KIND_LABEL,
  INSPECTION_RESULT_LABEL,
  PROJECT_STATUS_LABEL,
  type Gate,
  type GateDecisionResult,
  type GateStatus,
  type ProjectDetail,
  type ProjectInspectionRow,
} from "../../domain/projects";
import { Button, Card, EmptyState, ErrorState, numClass, Skeleton, Table } from "../../design/components";
import { formatDate, formatDateTime } from "../../lib/format";
import { t, tn } from "../../i18n";

/**
 * One project: its schedule against its baseline, the money as declared, its
 * gates, and the inspections — with the hold points among them marked as what
 * they are.
 */
export function ProjectDetailPage() {
  const { id = "" } = useParams();
  const project = useProject(id);
  const { principal } = useSession();
  const [editing, setEditing] = useState<"details" | "schedule" | null>(null);
  const canWrite = hasPermission(principal, PERMISSION.projectsWrite);

  if (project.isPending) return <Skeleton rows={8} height={20} />;
  if (project.isError) return <ErrorState error={project.error} retry={() => void project.refetch()} />;

  const p = project.data;
  return (
    <Page
      eyebrow={t("{site} · capital project · {code}", { site: p.siteCode ?? "", code: p.code }).replace(/^ · /, "")}
      title={p.name}
      actions={
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Button
            onClick={() => setEditing("details")}
            disabled={!canWrite}
            title={
              canWrite
                ? t("Status, description, money, contractor")
                : t("Requires {permission}", { permission: PERMISSION.projectsWrite })
            }
          >
            {t("Edit details")}
          </Button>
          <Button
            onClick={() => setEditing("schedule")}
            disabled={!canWrite}
            title={
              canWrite
                ? t("Current dates and the baseline")
                : t("Requires {permission}", { permission: PERMISSION.projectsWrite })
            }
          >
            {t("Schedule")}
          </Button>
          <Link to="/capital-projects">{t("← Capital projects")}</Link>
        </div>
      }
    >
      {editing === "details" && <EditProjectDialog project={p} onClose={() => setEditing(null)} />}
      {editing === "schedule" && <ScheduleDialog project={p} onClose={() => setEditing(null)} />}
      {p.workStopped && (
        <div className={s.stopBanner} data-testid="project-stopped">
          <div className={s.stopTitle}>
            {t("Work stopped — {count}", {
              count: tn(p.holding, "{n} unreleased hold point", "{n} unreleased hold points"),
            })}
          </div>
          {p.holdPoints.map((h) => (
            <div key={h.id} className={s.note}>
              <Link to={`/capital-projects/inspections/${h.id}`}>{h.reference}</Link> {h.title} — {h.holdingBecause}
            </div>
          ))}
        </div>
      )}

      <div className={s.split}>
        <Card title="Schedule" sub={p.schedule.why ?? undefined}>
          <div style={{ marginBottom: 10 }}>
            <ScheduleCell p={p} />
          </div>
          <dl className={s.facts}>
            <dt>{t("Baseline")}</dt>
            <dd>
              {formatDate(p.schedule.baselineStart)} → {formatDate(p.schedule.baselineEnd)}
            </dd>
            <dt>{t("Current")}</dt>
            <dd>
              {formatDate(p.schedule.startDate)} → {formatDate(p.schedule.endDate)}
            </dd>
            <dt>{t("Status")}</dt>
            <dd>{t(PROJECT_STATUS_LABEL[p.status])}</dd>
          </dl>
          {p.schedule.rebaselineNote && (
            <div className={s.warn} data-testid="rebaseline-note">
              {p.schedule.rebaselineNote}
            </div>
          )}
          {p.rebaselines.length > 0 && (
            <div style={{ marginTop: 8 }} data-testid="rebaselines">
              {p.rebaselines.map((r, i) => (
                <div key={i} className={s.said}>
                  {t("Baseline was {start} → {end}, moved", {
                    start: formatDate(r.baselineStart),
                    end: formatDate(r.baselineEnd),
                  })}{" "}
                  {r.replacedAt ? formatDate(r.replacedAt) : t("at an unrecorded time")}
                  {r.replacedBy ? ` ${t("by {name}", { name: r.replacedBy })}` : ""}:{" "}
                  {r.reason ?? t("no reason recorded")}
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="Money" sub="as declared — the platform computes nothing from these and neither does this screen">
          {p.money.state === "not_declared" ? (
            <EmptyState>
              {t(
                "NOT DECLARED: no budget, commitment or spend recorded yet. This isn't a zero budget.",
              )}
            </EmptyState>
          ) : (
            <>
              <div className={s.money}>
                <div className={s.moneyCell}>
                  <span className={s.moneyLabel}>{t("Budget")}</span>
                  <span className={s.figure}>{formatMoney(p.money.budgetMinor, p.money.currency)}</span>
                </div>
                <div className={s.moneyCell}>
                  <span className={s.moneyLabel}>{t("Committed")}</span>
                  <span className={s.figure}>{formatMoney(p.money.committedMinor, p.money.currency)}</span>
                </div>
                <div className={s.moneyCell}>
                  <span className={s.moneyLabel}>{t("Spent")}</span>
                  <span className={s.figure}>{formatMoney(p.money.spentMinor, p.money.currency)}</span>
                </div>
              </div>
              {/* No variance, no remaining, no percentage: the platform is not told
                  whether committed includes spent, so every one of those would be a
                  guess wearing the clothes of a calculation. */}
              <div className={s.said} style={{ marginTop: 8 }}>
                {p.money.note}
              </div>
            </>
          )}
        </Card>

        <Card title="Project">
          <dl className={s.facts}>
            <dt>{t("Code")}</dt>
            <dd className="mono">{p.code}</dd>
            <dt>{t("Category")}</dt>
            <dd>{p.category ?? "—"}</dd>
            <dt>{t("Owner")}</dt>
            <dd>{p.ownerName ?? "—"}</dd>
            <dt>{t("Contractor")}</dt>
            <dd>{p.contractor ?? "—"}</dd>
            <dt>{t("Consultant")}</dt>
            <dd>{p.consultant ?? "—"}</dd>
            <dt>{t("Gate set")}</dt>
            <dd className="mono">
              {p.gateSet ? `${p.gateSet.key}${p.gateSet.version ? ` v${p.gateSet.version}` : ""}` : "—"}
            </dd>
          </dl>
          {p.description && (
            <div className={s.said} style={{ marginTop: 8 }}>
              {p.description}
            </div>
          )}
        </Card>
      </div>

      <ProjectLogCard projectId={p.id} />

      <GatesCard p={p} />
      <InspectionsCard p={p} />

      {p.links.length > 0 && (
        <Card title="What this project touches" sub="recorded so the project is findable from the thing it affects">
          <Table>
            <thead>
              <tr>
                <th>{t("Kind")}</th>
                <th>{t("Reference")}</th>
                <th>{t("Name")}</th>
                <th>{t("Relation")}</th>
              </tr>
            </thead>
            <tbody>
              {p.links.map((l) => (
                <tr key={`${l.entityKind}-${l.entityId}`}>
                  <td className="mono">{l.entityKind}</td>
                  <td className="mono">{l.entityRef ?? l.entityId}</td>
                  <td>{l.entityName ?? "—"}</td>
                  <td className="muted">{l.relation ?? t("affects")}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </Page>
  );
}

function GatesCard({ p }: { p: ProjectDetail }) {
  const [decided, setDecided] = useState<GateDecisionResult | null>(null);
  return (
    <Card
      title="Stage gates"
      sub={
        p.gateSummary.state === "no_gates"
          ? (p.gateSummary.why ?? t("no gates"))
          : p.gateSummary.nextGate
            ? t("{decided} of {count} decided · next: {gate}", {
                decided: p.gateSummary.decided,
                count: p.gateSummary.count,
                gate: p.gateSummary.nextGate,
              })
            : t("{decided} of {count} decided · all decided", {
                decided: p.gateSummary.decided,
                count: p.gateSummary.count,
              })
      }
    >
      {p.gateSummary.decidedWithoutEvidence.length > 0 && (
        <div className={s.bad} style={{ marginBottom: 8 }} data-testid="gates-unevidenced">
          {t(
            "Decided without the required evidence: {gates}. This stays on each gate for the life of the project.",
            {
              gates: p.gateSummary.decidedWithoutEvidence.join(", "),
            },
          )}
        </div>
      )}
      {decided && decided.warnings.length > 0 && (
        <div className={s.warn} role="status" data-testid="decision-warnings">
          {decided.warnings.map((w, i) => (
            <div key={i}>{w}</div>
          ))}
        </div>
      )}
      {p.gateList.length === 0 ? (
        <EmptyState>{p.gateSummary.why}</EmptyState>
      ) : (
        <div className={s.gates}>
          {p.gateList.map((g) => (
            <GateRow
              key={g.key}
              projectId={p.id}
              gate={g}
              isNext={p.gateSummary.nextGate === g.key}
              onDecided={setDecided}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

function GateRow({
  projectId,
  gate,
  isNext,
  onDecided,
}: {
  projectId: string;
  gate: Gate;
  isNext: boolean;
  onDecided: (r: GateDecisionResult) => void;
}) {
  const { principal } = useSession();
  const [deciding, setDeciding] = useState(false);
  const submit = useSubmitGate(projectId);

  /* Three permissions, three buttons, each disabled on its own key. */
  const mayWrite = hasPermission(principal, PERMISSION.projectsWrite);
  const mayDecide = hasPermission(principal, PERMISSION.projectsGate);

  return (
    <div className={`${s.gate} ${isNext ? s.gateNext : ""}`} data-testid={`gate-${gate.key}`}>
      <span className={s.gateSeq}>{gate.seq}</span>
      <div>
        <div className={s.gateName}>{gate.name}</div>
        <div className={s.gateMeta}>
          <span>{gate.key}</span>
          {gate.dueDate && <span>{t("due {date}", { date: formatDate(gate.dueDate) })}</span>}
          {gate.submittedAt && <span>{t("submitted {date}", { date: formatDate(gate.submittedAt) })}</span>}
          {gate.decidedAt && <span>{t("decided {date}", { date: formatDate(gate.decidedAt) })}</span>}
          {gate.decidedByName && <span>{t("by {name}", { name: gate.decidedByName })}</span>}
        </div>
        {gate.evidence.required.length > 0 && (
          <div className={s.chips}>
            {gate.evidence.required.map((k) => (
              <span
                key={k}
                className={`${s.chip} ${gate.evidence.supplied.includes(k) ? s.chipSupplied : s.chipMissing}`}
              >
                {k}
              </span>
            ))}
          </div>
        )}
        {/* The platform's own sentence about this gate's evidence, whichever of
            the five states it is in — including the one that says it was decided
            anyway and what was absent. */}
        {gate.evidence.why && (
          <div className={gate.evidence.state === "decided_without_evidence" ? s.bad : s.said}>{gate.evidence.why}</div>
        )}
        {gate.decisionNote && <div className={s.said}>“{gate.decisionNote}”</div>}
        {submit.isError && (
          <div className={s.bad} role="alert">
            {t("Refused: {message}", { message: (submit.error as Error).message })}
          </div>
        )}
      </div>
      <div className={s.actions}>
        <span className={s.state} style={{ "--st-c": gateColour(gate.status) } as CSSProperties}>
          {t(GATE_STATUS_LABEL[gate.status])}
        </span>
        {canSubmitGate(gate) && (
          <Button
            size="sm"
            disabled={!mayWrite || submit.isPending}
            title={mayWrite ? undefined : t("Needs the project:write permission")}
            onClick={() => submit.mutate({ gateKey: gate.key })}
            aria-label={t("Submit {gate} for decision", { gate: gate.name })}
          >
            {t("Submit for decision")}
          </Button>
        )}
        <Button
          size="sm"
          variant={isNext ? "primary" : "default"}
          disabled={!mayDecide}
          title={
            mayDecide ? undefined : t("Needs the project:gate permission — deciding a gate is not writing a project")
          }
          onClick={() => setDeciding(true)}
          aria-label={t("Decide {gate}", { gate: gate.name })}
        >
          {gate.status === "pending" || gate.status === "submitted" ? t("Decide") : t("Decide again")}
        </Button>
      </div>
      {deciding && (
        <GateDecisionDialog
          projectId={projectId}
          gate={gate}
          onClose={() => setDeciding(false)}
          onDecided={(r) => {
            onDecided(r);
            setDeciding(false);
          }}
        />
      )}
    </div>
  );
}

function InspectionsCard({ p }: { p: ProjectDetail }) {
  if (p.inspections.length === 0)
    return (
      <Card title="Inspections">
        <EmptyState>
          {t(
            "No inspections recorded yet. That doesn't mean they passed.",
          )}
        </EmptyState>
      </Card>
    );
  return (
    <Card
      title="Inspections"
      sub={t("{count} · hold points stop the work until released", {
        count: tn(p.inspections.length, "{n} record", "{n} records"),
      })}
    >
      <Table>
        <thead>
          <tr>
            <th>{t("Reference")}</th>
            <th>{t("Kind")}</th>
            <th>{t("Discipline")}</th>
            <th>{t("Result")}</th>
            <th>{t("Sheet")}</th>
            <th>{t("Performed")}</th>
            <th>{t("State")}</th>
          </tr>
        </thead>
        <tbody>
          {p.inspections.map((i) => (
            <InspectionRowView key={i.id} i={i} />
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

function InspectionRowView({ i }: { i: ProjectInspectionRow }) {
  return (
    <tr className={i.stoppingWork ? s.stopped : undefined} data-testid={`inspection-${i.reference}`}>
      <td>
        <Link to={`/capital-projects/inspections/${i.id}`}>{i.reference}</Link>
        <div className="muted" style={{ fontSize: 13 }}>
          {i.title}
        </div>
      </td>
      <td>
        <span
          className={s.state}
          style={{ "--st-c": i.kind === "hold" ? "var(--red)" : "var(--muted)" } as CSSProperties}
        >
          {t(INSPECTION_KIND_LABEL[i.kind])}
        </span>
      </td>
      <td className="muted">{i.discipline}</td>
      <td>
        {i.result ? (
          <span className={s.state} style={{ "--st-c": resultColour(i.result) } as CSSProperties}>
            {t(INSPECTION_RESULT_LABEL[i.result])}
          </span>
        ) : (
          <span className="muted">{t("not inspected")}</span>
        )}
      </td>
      <td className={numClass}>
        {i.items === 0 ? (
          <span
            className="muted"
            title={t("This inspection has no items. That is not an inspection with nothing wrong.")}
          >
            {t("no sheet")}
          </span>
        ) : (
          <>
            {i.items - i.notChecked}/{i.items}
            {i.failed > 0 && <span style={{ color: "var(--red)" }}> · {t("{n} failed", { n: i.failed })}</span>}
          </>
        )}
      </td>
      <td style={{ whiteSpace: "nowrap" }}>{i.performedAt ? formatDateTime(i.performedAt) : "—"}</td>
      <td className={s.said} style={{ maxWidth: 320 }}>
        {i.stoppingWork
          ? i.holdingBecause
          : i.releasedAt
            ? i.releasedByName
              ? t("released {date} by {name}", { date: formatDate(i.releasedAt), name: i.releasedByName })
              : t("released {date}", { date: formatDate(i.releasedAt) })
            : ""}
      </td>
    </tr>
  );
}

function gateColour(status: GateStatus): string {
  if (status === "approved") return "var(--green)";
  if (status === "rejected") return "var(--red)";
  if (status === "waived") return "var(--amber)";
  if (status === "submitted") return "var(--blue)";
  return "var(--dim)";
}

function resultColour(result: NonNullable<ProjectInspectionRow["result"]>): string {
  if (result === "fail") return "var(--red)";
  if (result === "pass") return "var(--green)";
  if (result === "not_applicable") return "var(--dim)";
  return "var(--amber)";
}
